import { useEffect, useRef, useState } from "react";
import Reveal from "@/components/site/Reveal";
import { useT } from "@/i18n";

const STATS = [
  { value: 3.5, suffix: "M+", decimals: 1 },
  { value: 28, suffix: "+", decimals: 0 },
  { value: 35, suffix: "+", decimals: 0 },
];

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function Counter({ target, suffix, decimals, start }) {
  const [val, setVal] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!start) return;
    const duration = 1400;
    const startTime = performance.now();
    const tick = (now) => {
      const elapsed = now - startTime;
      const p = Math.min(elapsed / duration, 1);
      setVal(target * easeOutCubic(p));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [start, target]);

  const formatted = decimals > 0 ? val.toFixed(decimals) : Math.floor(val).toString();
  return (
    <span>
      {formatted}
      {suffix}
    </span>
  );
}

export default function StatsSection() {
  const t = useT();
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-black text-white py-24 md:py-32 border-t border-white/5"
      data-testid="stats-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <Reveal className="text-center mb-16 md:mb-20">
          <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.5em] text-[#C20E1A] font-bold">
            {t.stats.eyebrow}
          </p>
          <h2 className="heading-display mt-5 text-3xl sm:text-4xl lg:text-5xl uppercase text-white">
            {t.stats.title1} <span className="text-[#C20E1A]">{t.stats.title2}</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-14 gap-x-6 lg:gap-x-10">
          {STATS.map((s, i) => (
            <Reveal
              key={i}
              delay={i * 90}
              className="text-center sm:border-l sm:border-white/10 sm:first:border-l-0 px-3 lg:px-6"
            >
              <p
                data-testid={`stat-value-${i}`}
                className="text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-bold tracking-tighter text-[#C20E1A] leading-none tabular-nums"
              >
                <Counter
                  target={s.value}
                  suffix={s.suffix}
                  decimals={s.decimals}
                  start={inView}
                />
              </p>
              <p className="mt-5 text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-white/70 font-bold">
                {t.stats.labels[i]}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
